import { Product } from '@interfaces';
import { extractExtraInfo } from '@screens/product/utils/extractExtraInfo';
import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';

import {
  RootDiv,
  TitleTypography,
  DetailsTextTypography,
  DetailsPriceDiv,
  DetailsPriceTypographyBold,
  DetailsPriceTypographyLight,
  ColumnDiv,
} from '../styles/ItemDetails.styles';
import {
  DISCOUNT_TYPES,
  discountText,
  DiscountTypeContainer,
} from '@screens/checkout/utils/discountTypeBanner';
import { Skeleton } from '@mui/material';

interface CartProductDetailsProps {
  product: Product;
  price: number;
  quantity: number;
  unitReimbursement?: number;
  unitDeductible?: number;
  unitCopay?: number;
  type?: string | null;
  listPrice?: number;
  loading?: boolean;
}

const currFormat = new CurrencyFormatter();

export function CartProductDetails({
  product,
  price,
  quantity,
  unitCopay,
  type,
  listPrice,
  loading,
}: CartProductDetailsProps) {
  const hasInsurance = unitCopay >= 0;
  const hasDiscount = product?.discount?.price > 0;
  const hasManualPrice =
    price !== product?.price && price !== product?.discount?.price;

  const isMekiPriceDifferent =
    listPrice && listPrice > 0
      ? listPrice
      : (hasDiscount && !hasManualPrice) || hasInsurance;
  const currentPrice = hasInsurance
    ? unitCopay
    : listPrice && listPrice > 0
    ? price
    : product?.discount?.price;
  const realMekiPrice =
    listPrice && listPrice > 0
      ? listPrice
      : hasInsurance || hasManualPrice || !hasDiscount
      ? price
      : Number(product?.discount?.listPrice);

  const discountType = type ?? product?.discount?.type;

  if (loading) {
    return <CartProductDetailsSkeleton />;
  }

  return (
    <RootDiv>
      <TitleTypography variant="subtitle2">{product.name}</TitleTypography>
      <DetailsTextTypography color="textSecondary" variant="body2">
        {extractExtraInfo(product)}
      </DetailsTextTypography>
      <DetailsTextTypography color="textPrimary" variant="subtitle2">
        {product.laboratory}
      </DetailsTextTypography>
      <ColumnDiv>
        <DetailsPriceDiv>
          <DetailsPriceTypographyBold
            type={isMekiPriceDifferent ? 'strikethrough' : 'normal'}
            color="textPrimary"
            variant="subtitle2"
          >
            {quantity > 1 && `${currFormat.format(realMekiPrice * quantity)}・`}
          </DetailsPriceTypographyBold>
          <DetailsPriceTypographyLight
            type={isMekiPriceDifferent ? 'strikethrough' : 'normal'}
            color="textPrimary"
            variant="subtitle2"
          >
            {`${currFormat.format(realMekiPrice)}`}/un
          </DetailsPriceTypographyLight>
        </DetailsPriceDiv>
        {isMekiPriceDifferent && (
          <DetailsPriceDiv>
            <DetailsPriceTypographyBold
              type="insurance"
              color="textPrimary"
              variant="subtitle2"
            >
              {quantity > 1 &&
                `${currFormat.format(currentPrice * quantity)}・`}
            </DetailsPriceTypographyBold>
            <DetailsPriceTypographyLight
              type="insurance"
              color="textPrimary"
              variant="subtitle2"
            >
              {`${currFormat.format(currentPrice)}`}/un
            </DetailsPriceTypographyLight>
          </DetailsPriceDiv>
        )}
        {discountType &&
          discountType != 'REGULAR' &&
          Object.values(DISCOUNT_TYPES).includes(discountType) && (
            <DiscountTypeContainer>
              {discountText(discountType)}
            </DiscountTypeContainer>
          )}
      </ColumnDiv>
    </RootDiv>
  );
}

export const CartProductDetailsSkeleton = () => (
  <RootDiv>
    <Skeleton variant="text" width="150px" height="19px" />
    <Skeleton variant="text" width="150px" height="19px" />
    <Skeleton variant="text" width="56px" height="18px" />
  </RootDiv>
);
